/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Segmented } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { indentedGroupStyle } from '../../indentedGroupStyle'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import CurrencyInput from 'src/components/ts-components/CurrencyInput'
import type { CountryLocaleInput } from '@sportninja/common/currency'
import { useRegistrationSetupContext } from '../../../context'
import {
  PREVIEW_BASE_COST,
  type ProcessingFeeBearer,
} from '../../../helpers/processingFees'
import FeePreview from './FeePreview'

type ProcessingFeesProps = {
  /** Combined rate of the competition's selected taxes, as a percentage. */
  taxRatePercent: number
  currencySymbol: string
  currencyCountry?: CountryLocaleInput
}

/**
 * Competition-wide processing fee configuration. Lives on the Registration
 * Setup → Payments tab only — these settings are never per-division.
 */
const ProcessingFees = ({
  taxRatePercent,
  currencySymbol,
  currencyCountry,
}: ProcessingFeesProps) => {
  'use memo'

  const { state, setState, apiAccount } = useRegistrationSetupContext()

  // The merchant rates are admin-gated: the keys are absent entirely unless the
  // viewer can administer the account (they are SportNinja's commercial terms
  // with the league). Treat a missing key as "not permitted" rather than zero,
  // so we never imply a rate we weren't given.
  const rawProviderFlat = apiAccount?.payment_provider_application_fee
  const rawProviderPercent = apiAccount?.payment_provider_percentage_fee
  const hasProviderRates =
    rawProviderFlat !== null &&
    rawProviderFlat !== undefined &&
    rawProviderPercent !== null &&
    rawProviderPercent !== undefined

  const providerFlat = Number(rawProviderFlat ?? 0)
  const providerPercent = Number(rawProviderPercent ?? 0)

  // Hide the rate sentence when the rates are withheld, and also when a
  // connected account reports 0 — rather than printing "$0.00 + 0%".
  const hasProviderRate =
    hasProviderRates && (providerFlat > 0 || providerPercent > 0)

  const absorbsProcessing = state.paymentProcessingFeeBearer === 'league'

  return (
    <div css={sectionStyle}>
      {/* Matches the "Payments" heading on this tab: same variant and weight,
          and no explicit color so it inherits the same default. */}
      <Text variant='captionLarge' weight='semiBold'>
        {capitalizeWords('Processing Fees').toUpperCase()}
      </Text>

      <div css={groupStyle}>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {capitalizeWords('Who Pays The Payment Processing Fee?')}
        </Text>
        {hasProviderRate ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {/* parseFloat(toFixed(4)) trims floating-point noise (e.g.
                2.9000000000000004) without forcing trailing zeros onto
                whole/short rates like 3 or 2.85. */}
            {`Your payment provider, charges ${parseFloat(
              providerPercent.toFixed(4)
            )}% + ${currencySymbol}${providerFlat.toFixed(
              2
            )} per transaction. Choose whether that comes out of your payout or is added to the registrant's total.`}
          </Text>
        ) : null}

        <Segmented
          css={segmentedToggleStyle}
          value={state.paymentProcessingFeeBearer}
          onChange={(value) => {
            setState((prev) => ({
              ...prev,
              paymentProcessingFeeBearer: value as ProcessingFeeBearer,
            }))
          }}
          options={[
            { label: capitalizeWords('League Absorbs It'), value: 'league' },
            {
              label: capitalizeWords('Registrant Pays It'),
              value: 'registrant',
            },
          ]}
        />

        <div css={switchRowStyle}>
          <SwitchInput
            title={capitalizeWords('Show As A Separate Line Item')}
            subtitle='Only applies when the registrant pays the fee.'
            value={state.paymentProcessingFeeVisible}
            onChange={(value) => {
              setState((prev) => ({
                ...prev,
                paymentProcessingFeeVisible: value,
              }))
            }}
            disabled={absorbsProcessing}
          />
        </div>
      </div>

      <div css={dividerStyle} />

      <div css={groupStyle}>
        <div css={switchRowStyle}>
          <SwitchInput
            title={capitalizeWords('Charge Additional League Processing Fee')}
            subtitle='An additional fee your league charges on each registration payment. Added on top of the registration cost, charged to the registrant, and paid out to you. Applies to every payment — the deposit, each installment, or a single full payment.'
            value={state.leagueProcessingFeeEnabled}
            onChange={(value) => {
              // Keep whatever was typed so flicking the switch off and back on
              // doesn't wipe unsaved input. The payload sends null while off.
              setState((prev) => ({
                ...prev,
                leagueProcessingFeeEnabled: value,
              }))
            }}
          />
        </div>

        {state.leagueProcessingFeeEnabled ? (
          <div css={indentedGroupStyle}>
            <div css={inputRowStyle}>
              <div css={inputCellStyle}>
                <TextInput
                  name='leagueProcessingFeePercent'
                  label={capitalizeWords('Percentage')}
                  type='number'
                  min={0}
                  max={100}
                  addonAfter='%'
                  placeholder='0.00'
                  value={
                    state.leagueProcessingFeePercent === null
                      ? ''
                      : String(state.leagueProcessingFeePercent)
                  }
                  setValue={(value) => {
                    setState((prev) => ({
                      ...prev,
                      leagueProcessingFeePercent:
                        value === '' ? null : Number(value),
                    }))
                  }}
                  fieldErrors={state.fieldErrors}
                  customError={state.customError}
                  style={percentInputStyle}
                />
              </div>
              <div css={inputCellStyle}>
                <CurrencyInput
                  name='leagueProcessingFeeFlat'
                  label={capitalizeWords('Flat Amount')}
                  value={state.leagueProcessingFeeFlat}
                  onChange={(value) => {
                    setState((prev) => ({
                      ...prev,
                      leagueProcessingFeeFlat: Number.isNaN(value)
                        ? null
                        : value,
                    }))
                  }}
                  min={0}
                  fieldErrors={state.fieldErrors}
                  customError={state.customError?.leagueProcessingFeeFlat || ''}
                  country={currencyCountry}
                />
              </div>
            </div>

            <div css={switchRowStyle}>
              <SwitchInput
                title={capitalizeWords('Show As A Separate Line Item')}
                subtitle='Registrants see “League Processing Fee” broken out on invoices, emails and payment history. Turn off to include it in the displayed registration cost instead — the fee is still charged.'
                value={state.leagueProcessingFeeVisible}
                onChange={(value) => {
                  setState((prev) => ({
                    ...prev,
                    leagueProcessingFeeVisible: value,
                  }))
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      <FeePreview
        currencySymbol={currencySymbol}
        input={{
          baseCost: PREVIEW_BASE_COST,
          leaguePercent: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeePercent
            : null,
          leagueFlat: state.leagueProcessingFeeEnabled
            ? state.leagueProcessingFeeFlat
            : null,
          leagueVisible: state.leagueProcessingFeeVisible,
          bearer: state.paymentProcessingFeeBearer,
          paymentVisible: state.paymentProcessingFeeVisible,
          taxRatePercent,
          providerFlat,
          providerPercent,
        }}
      />
    </div>
  )
}

export default ProcessingFees

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 100%;
`

const groupStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`

const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`

// Everything the league fee's master switch owns — percentage, flat amount and
// the line-item toggle — is nested behind the shared left rail (see
// `../../indentedGroupStyle`, hoisted out of here when General Settings became a
// second consumer). Without it the three read as unrelated fields that happen to
// sit nearby. The payment processing block above is deliberately NOT indented:
// it has no enable switch because it always applies, and that difference in
// indentation is what tells an admin which fields are conditional.

const inputRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const inputCellStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`

const dividerStyle = css`
  align-self: stretch;
  height: 1px;
  background-color: ${colors.NEUTRAL_100};
`
// TextInput sets no placeholder color, so it falls back to antd's default grey —
// visibly lighter than the sibling CurrencyInput, which uses NEUTRAL_100. Match
// it here (rather than in the shared TextInput, which every other form uses) and
// blend the `%` addon into the field so the two inputs read as a matched pair.
const percentInputStyle = css`
  & input::placeholder {
    color: ${colors.NEUTRAL_100};
  }
  .ant-input-group-addon {
    background: ${colors.SECONDARY_200};
    border-color: ${colors.SECONDARY_100};
    color: ${colors.NEUTRAL_30};
  }
`

// Antd v5's default Segmented palette doesn't fit this dark surface. Override
// the track, thumb and label colors. Sized to its content (no `block`) with a
// taller item so the two options read as tab buttons rather than a full-width
// slider.
const segmentedToggleStyle = css`
  align-self: flex-start;
  &.ant-segmented {
    background-color: ${colors.SECONDARY_200};
    padding: 3px;
    border-radius: 8px;
  }
  .ant-segmented-item {
    color: ${colors.NEUTRAL_30};
    transition: color 0.15s;
    border-radius: 6px;
  }
  .ant-segmented-item:hover:not(.ant-segmented-item-disabled) {
    color: ${colors.WHITE};
  }
  .ant-segmented-item-selected {
    background-color: ${colors.PRIMARY} !important;
    color: ${colors.SECONDARY_300} !important;
    font-weight: 600;
  }
  .ant-segmented-item-label {
    line-height: 34px;
    padding: 0 20px;
  }
`
